import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import { LenderDashboard } from "@/components/admin/lenders/LenderDashboard";

export default function PublicLenderDashboard() {
  const [searchParams] = useSearchParams();
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessData, setAccessData] = useState<{
    email: string;
    lenderProfileId: string | null;
  } | null>(null);

  useEffect(() => {
    validateToken();
  }, [searchParams]);

  const validateToken = async () => {
    const token = searchParams.get("token");

    if (!token) {
      setError("No access token provided");
      setIsValidating(false);
      return;
    }

    try {
      const { data, error: queryError } = await supabase
        .from("lender_dashboard_access")
        .select("*")
        .eq("access_token", token)
        .single();

      if (queryError || !data) {
        setError("Invalid or expired access token");
        setIsValidating(false);
        return;
      }

      // Check if token is expired
      if (new Date(data.expires_at) < new Date()) {
        setError("Access token has expired");
        setIsValidating(false);
        return;
      }

      // Update last accessed timestamp
      await supabase
        .from("lender_dashboard_access")
        .update({ last_accessed_at: new Date().toISOString() })
        .eq("id", data.id);

      setAccessData({
        email: data.email,
        lenderProfileId: data.lender_profile_id,
      });
      setIsValidating(false);
    } catch (err) {
      console.error("Error validating token:", err);
      setError("Failed to validate access token");
      setIsValidating(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            <p>Validating access...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground mt-4">
            Please contact your administrator for assistance.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">
            {accessData?.lenderProfileId ? "Your Lender Dashboard" : "Lender Dashboard"}
          </h1>
          <p className="text-muted-foreground">
            Viewing as: {accessData?.email}
          </p>
        </div>
        <LenderDashboard lenderProfileId={accessData?.lenderProfileId || undefined} />
      </div>
    </div>
  );
}
